// Empire Apps Chassis · connectors/health/garmin-stub
// Garmin Connect has no public retail API. Two real paths:
//   1. CSV export from Garmin Connect web (user-initiated).
//   2. Connect IQ developer programme (commercial, gated).
//
// This connector accepts a CSV upload and parses sleep + steps + heart rate.
// Live OAuth path deferred to v1.1 (commercial agreement needed).

import {
  type ConnectorAdapter,
  type ConnectorMode,
  type ConnectorResult,
  type ConnectorStatus,
  err,
  nowIso,
  ok,
  shortHash,
} from './_types.ts';

export interface GarminCsvRow {
  readonly date: string;
  readonly sleepHours: number;
  readonly steps: number;
  readonly avgHeartRate: number;
  readonly maxHeartRate: number;
  readonly stress: number | null;
}

export interface GarminPayload {
  readonly rows: readonly GarminCsvRow[];
  readonly importedFrom: 'manual-csv';
  readonly ingestedAt: string;
}

export interface GarminStatus extends ConnectorStatus {
  readonly id: 'garmin-stub';
  readonly rowsCached: number;
}

export class GarminStubConnector implements ConnectorAdapter<GarminStatus, GarminPayload> {
  readonly id = 'garmin-stub';
  readonly displayName = 'Garmin Connect';
  readonly displayNameHi = 'Garmin · CSV upload';
  readonly dpdpCategory = 'PHI-medium · activity + sleep';
  readonly mode: ConnectorMode = 'test';
  private rows: readonly GarminCsvRow[] = [];
  private lastSync: string | null = null;
  private principalHash: string | null = null;

  constructor(private readonly principalId: string) {}

  async getStatus(): Promise<GarminStatus> {
    if (!this.principalHash) this.principalHash = await shortHash(this.principalId);
    return {
      id: this.id,
      mode: this.mode,
      connected: this.rows.length > 0,
      lastSyncAt: this.lastSync,
      principalIdHash: this.principalHash,
      notes: this.rows.length > 0 ? `${this.rows.length} rows cached` : 'export CSV from Garmin Connect',
      rowsCached: this.rows.length,
    };
  }

  async connect(): Promise<ConnectorResult<GarminStatus>> {
    return ok(await this.getStatus());
  }

  async ingestCsv(csv: string): Promise<ConnectorResult<GarminPayload>> {
    try {
      const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) return err({ code: 'parse', message: 'csv empty', retriable: false });
      const header = (lines[0] ?? '').toLowerCase();
      const dateIdx = header.split(',').findIndex((h) => h.trim() === 'date');
      if (dateIdx < 0) return err({ code: 'parse', message: 'no date column', retriable: false });
      const rows: GarminCsvRow[] = [];
      for (const ln of lines.slice(1)) {
        const cols = ln.split(',');
        const date = cols[dateIdx]?.trim();
        if (!date) continue;
        rows.push({
          date,
          sleepHours: numericAfter(cols, header.split(','), 'sleep') / 60,
          steps: numericAfter(cols, header.split(','), 'steps'),
          avgHeartRate: numericAfter(cols, header.split(','), 'avg heart'),
          maxHeartRate: numericAfter(cols, header.split(','), 'max heart'),
          stress: numericAfter(cols, header.split(','), 'stress') || null,
        });
      }
      this.rows = rows;
      this.lastSync = nowIso();
      return ok({ rows, importedFrom: 'manual-csv', ingestedAt: nowIso() });
    } catch (e) {
      return err({ code: 'parse', message: String(e instanceof Error ? e.message : e), retriable: false });
    }
  }

  async importPayload(): Promise<ConnectorResult<GarminPayload>> {
    if (this.rows.length === 0) return ok(this.mockPayload());
    return ok({ rows: this.rows, importedFrom: 'manual-csv', ingestedAt: this.lastSync ?? nowIso() });
  }

  async disconnect(): Promise<void> {
    this.rows = [];
    this.lastSync = null;
  }

  mockPayload(): GarminPayload {
    const today = new Date();
    const rows: GarminCsvRow[] = [];
    for (let d = 13; d >= 0; d--) {
      const date = new Date(today);
      date.setDate(today.getDate() - d);
      rows.push({
        date: date.toISOString().slice(0, 10),
        sleepHours: 6.6 + (d % 3) * 0.3,
        steps: 7000 + (d % 5) * 800,
        avgHeartRate: 68 + (d % 4),
        maxHeartRate: 138 + (d % 6) * 4,
        stress: 28 + (d % 7) * 3,
      });
    }
    return { rows, importedFrom: 'manual-csv', ingestedAt: nowIso() };
  }
}

function numericAfter(cols: readonly string[], headers: readonly string[], keyword: string): number {
  const idx = headers.findIndex((h) => h.trim().toLowerCase().includes(keyword));
  if (idx < 0) return 0;
  const raw = (cols[idx] ?? '').replace(/,/g, '').trim();
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}
